import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { listIssues, listLabelGroups, listLabels, listRepositories, moveIssue } from '@mkcore/dev/schema';
import {
  Card,
  EmptyState,
  ErrorState,
  Field,
  Mono,
  NativeSelect,
  PageHeader,
  Skeleton,
  cn,
} from '@mkcore/ui';
import { call } from '@/lib/api';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { boardScrollerClass } from '@/components/layout';
import { connectionsQuery } from '@/features/admin/connections-panel';
import { ItemAgentBadge, useItemAgentStatus } from '@/features/agents/item-status';

const search = z.object({
  group: z.string().optional(),
  repositoryId: z.string().optional(),
  assignee: z.string().optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/dev/board')({
  validateSearch: search,
  staticData: { fullWidth: true },
  component: Board,
});

const reposQuery = queryOptions({
  queryKey: ['dev', 'repo', 'list', 'enabled'],
  queryFn: () => call(listRepositories, { enabled: true }),
});
const groupsQuery = queryOptions({
  queryKey: ['dev', 'labelGroup', 'list'],
  queryFn: () => call(listLabelGroups, {}),
});
const NONE = '';
/** At most this many open issues on one board: four pages of the list. */
const MAX_PAGES = 4;

/**
 * A label board (CO-0393): the open issues in a column per label of one label group (tier, severity, area, phase,
 * triage). Dragging a card, or choosing a column in its menu, gives the issue that label and takes the other labels of
 * the group off, in Forgejo with your linked account. Only groups a board may change are movable; the service checks
 * that again.
 */
function Board() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const message = useErrorMessage();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const repos = useQuery(reposQuery);
  const groups = useQuery(groupsQuery);
  const labels = useQuery({
    queryKey: ['dev', 'label', 'list', s.repositoryId ?? 'all'],
    queryFn: () => call(listLabels, s.repositoryId ? { repositoryId: s.repositoryId } : {}),
  });
  const withLabels = (groups.data?.items ?? []).filter((g) => g.labels > 0);
  const group =
    withLabels.find((g) => g.name === s.group) ?? withLabels.find((g) => g.writable) ?? withLabels[0];
  const issues = useQuery({
    queryKey: ['dev', 'issue', 'board', s.repositoryId, s.assignee],
    queryFn: async () => {
      const items = [];
      let cursor: string | undefined;
      for (let n = 0; n < MAX_PAGES; n++) {
        const page = await call(listIssues, {
          state: 'open',
          sort: 'updated',
          limit: 200,
          ...(s.repositoryId && { repositoryId: s.repositoryId }),
          ...(s.assignee && { assignee: s.assignee }),
          ...(cursor && { cursor }),
        });
        items.push(...page.items);
        if (!page.nextCursor) return { items, more: false };
        cursor = page.nextCursor;
      }
      return { items, more: true };
    },
  });
  const conn = useQuery(connectionsQuery);
  const linked = conn.data?.items.some((c) => c.provider === 'forgejo' && c.status === 'active') ?? false;
  const canUpdate = useCan('dev:update');
  const movable = linked && canUpdate && !!group?.writable;
  const move = useActionMutation(moveIssue, { invalidate: [['dev', 'issue']] });
  const [over, setOver] = useState<string | null>(null);
  const agentStatus = useItemAgentStatus('issue', issues.data?.items.map((i) => i.id) ?? []);

  if (issues.isError) return <ErrorState title={t('errors.generic')} description={message(issues.error)} />;
  const columnLabels = group
    ? [...new Set((labels.data?.items ?? []).filter((l) => l.group === group.name).map((l) => l.name))].sort()
    : [];
  const columns = [
    ...columnLabels.map((l) => ({ key: l, title: l })),
    { key: NONE, title: t('board.none', { group: group?.name ?? '' }) },
  ];
  const columnOf = (issueLabels: string[]) => columnLabels.find((l) => issueLabels.includes(l)) ?? NONE;
  const drop = (issueId: string, key: string) => {
    const issue = issues.data?.items.find((i) => i.id === issueId);
    if (!issue || !group || columnOf(issue.labels) === key) return;
    move.mutate({ input: { id: issueId, group: group.name, label: key || null } });
  };

  return (
    <>
      <PageHeader eyebrow={t('nav.dev')} title={t('board.title')} description={t('board.description')} />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('board.group')} className="w-48">
          {(p) => (
            <NativeSelect {...p} value={group?.name ?? ''} onChange={(e) => set({ group: e.target.value })}>
              {withLabels.map((g) => (
                <option key={g.name} value={g.name}>
                  {g.title || g.name}
                  {g.writable ? '' : ` (${t('board.readOnly')})`}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('issues.repository')} className="w-56">
          {(p) => (
            <NativeSelect
              {...p}
              value={s.repositoryId ?? ''}
              onChange={(e) => set({ repositoryId: e.target.value || undefined })}
            >
              <option value="">{t('common.all')}</option>
              {(repos.data?.items ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.fullName}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      </div>
      {group && !group.writable && (
        <p className="mb-3 text-sm text-fg-3">{t('board.notWritable', { group: group.name })}</p>
      )}
      {group?.writable && !linked && conn.data && (
        <p className="mb-3 text-sm text-fg-3">
          {t('issues.linkFirst')}{' '}
          <Link to="/settings" className="underline">
            {t('nav.settings')}
          </Link>
        </p>
      )}
      {issues.data?.more && (
        <p className="mb-3 text-sm text-warning">
          {t('board.truncated', { count: issues.data.items.length })}
        </p>
      )}
      {!groups.data || !issues.data ? (
        <Skeleton className="h-96" />
      ) : !group ? (
        <EmptyState title={t('board.noGroups')} />
      ) : (
        <div className={boardScrollerClass} role="list" aria-label={t('board.columns')}>
          {columns.map((col) => {
            const cards = issues.data.items.filter((i) => columnOf(i.labels) === col.key);
            return (
              <section
                key={col.key || '-'}
                role="listitem"
                aria-label={t('board.column', { title: col.title, count: cards.length })}
                className={cn(
                  'flex w-72 shrink-0 flex-col gap-2 rounded-lg bg-sunken p-2',
                  over === col.key && 'ring-2 ring-gold',
                )}
                onDragOver={(e) => {
                  if (!movable) return;
                  e.preventDefault();
                  setOver(col.key);
                }}
                onDragLeave={() => setOver((o) => (o === col.key ? null : o))}
                onDrop={(e) => {
                  e.preventDefault();
                  setOver(null);
                  const id = e.dataTransfer.getData('text/x-issue');
                  if (id) drop(id, col.key);
                }}
              >
                <h2 className="flex items-baseline justify-between px-1 text-sm font-bold">
                  <span>{col.title}</span>
                  <span className="text-xs font-normal text-fg-3 tabular">{cards.length}</span>
                </h2>
                {cards.map((i) => (
                  <Card
                    key={i.id}
                    draggable={movable}
                    data-testid={`card-${i.number}`}
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/x-issue', i.id);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    className={cn(
                      'grid gap-1 p-2 text-sm',
                      movable && 'cursor-grab',
                      move.isPending && move.variables?.input.id === i.id && 'opacity-50',
                    )}
                  >
                    <Link to="/dev/issues/$id" params={{ id: i.id }} className="font-bold hover:underline">
                      {i.title}
                    </Link>
                    <div className="flex flex-wrap items-center gap-1 text-xs text-fg-3">
                      <Mono className="text-[11px]">
                        {i.repository}#{i.number}
                      </Mono>
                      {i.assignees.length > 0 && <span>· {i.assignees.join(', ')}</span>}
                    </div>
                    <ItemAgentBadge status={agentStatus.get(i.id)} />
                    {movable && (
                      <NativeSelect
                        aria-label={t('board.moveTo', { number: i.number })}
                        className="h-7 text-xs"
                        value={col.key}
                        disabled={move.isPending}
                        onChange={(e) => drop(i.id, e.target.value)}
                      >
                        {columns.map((c) => (
                          <option key={c.key || '-'} value={c.key}>
                            {c.title}
                          </option>
                        ))}
                      </NativeSelect>
                    )}
                  </Card>
                ))}
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}
